import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { Markdown } from '../components/Markdown.js';
import { useT } from '../i18n/useT.js';

interface SummaryItem {
  sessionId: string;
  projectId: string;
  projectName: string;
  user: string;
  summary: string;
  model: string;
  frustrationScore: number;
  durationMs: number;
  createdAt?: string;
}

export function AISummariesPage() {
  const t = useT().alerts.summaries;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const base = `/v1/orgs/${orgId}/summaries`;

  const list = useQuery({
    enabled: !!orgId,
    queryKey: ['summaries', orgId],
    queryFn: async () => (await api.get(base)).data.data as SummaryItem[],
  });

  const generate = useMutation({
    mutationFn: async () => (await api.post(`${base}/generate`, { count: 6 })).data as { generated: number; failed: number; error?: string },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['summaries', orgId] }),
  });

  const createTicket = useMutation({
    mutationFn: async (s: SummaryItem) =>
      (await api.post(`/v1/orgs/${orgId}/tickets`, {
        title: `Review session, ${s.user}`,
        priority: s.frustrationScore >= 60 ? 'CRITICAL' : 'HIGH',
        projectId: s.projectId,
        sessionId: s.sessionId,
        description: s.summary,
      })).data as { id: string },
    onSuccess: (t) => navigate(`/tickets/${t.id}`),
  });

  const rows = list.data ?? [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h5">{t.title}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => navigate('/ai-settings')}>
            {t.configure}
          </Button>
          <Button variant="contained" startIcon={<AutoAwesomeIcon />} disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? t.generating : t.generateMore}
          </Button>
        </Stack>
      </Stack>

      {(list.isLoading || generate.isPending) && <LinearProgress sx={{ mb: 2 }} />}
      {generate.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{t.failed}</Alert>
      )}
      {generate.data?.error ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => navigate('/ai-settings')}>{t.aiSettings}</Button>}
        >
          {generate.data.generated > 0
            ? t.partial(generate.data.generated, generate.data.failed, generate.data.error)
            : t.error(generate.data.error)}
        </Alert>
      ) : generate.data ? (
        <Alert severity={generate.data.generated > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
          {generate.data.generated > 0 ? t.generated(generate.data.generated) : t.nothingNew}
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        {rows.map((s) => (
          <Paper key={s.sessionId} sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{s.user}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.projectName}</Typography>
                  <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />} label={s.model} variant="outlined" />
                  {s.frustrationScore > 0 && (
                    <Chip size="small" label={t.frustration(s.frustrationScore)} sx={{ bgcolor: frustBg(s.frustrationScore), color: '#fff' }} />
                  )}
                  <Typography variant="caption" color="text.disabled">{Math.round(s.durationMs / 1000)}s</Typography>
                </Stack>
                <Markdown text={s.summary} />
              </Box>
              <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
                <Button size="small" startIcon={<PlayArrowIcon />} onClick={() => navigate(`/projects/${s.projectId}/sessions/${s.sessionId}`)}>
                  {t.watch}
                </Button>
                <Button size="small" startIcon={<ConfirmationNumberIcon />} disabled={createTicket.isPending} onClick={() => createTicket.mutate(s)}>
                  {t.ticket}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
        {!list.isLoading && rows.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>{t.emptyTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t.emptyBody}
            </Typography>
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => generate.mutate()}>{t.emptyCta}</Button>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}

function frustBg(score: number): string {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#64748b';
}
